import { Box, Typography } from "@avenue-8/sidekick-ui";
import {
  PropertyPhoto,
  SideBySideComparisonSectionView,
} from "@cma/presentation-generation-logic";
import { ComparisonPropertyItem } from "./components/ComparisonPropertyItem";
import { useEffect, useMemo } from "react";
import { PresentationSectionViewProps } from "../PresentationSectionProps";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  SideBySideComparisonList,
  SideBySideComparisonHeader,
  NavButtonsContainer,
  NavButton,
  SlideItem,
} from "./SideBySideComparison.styles";
import { SideBySideComparisonCarousel } from "./components/SideBySideComparisonCarousel";
import { useScrollableCarousel } from "./hooks/useScrollableCarousel";

interface Props
  extends PresentationSectionViewProps<SideBySideComparisonSectionView> {
  galleries: { [key: string]: PropertyPhoto[] };
}

export const SideBySideComparisonView = (props: Props) => {
  const {
    galleries,
    sectionView: { id, preferences, properties },
  } = props;

  const {
    containerRef,
    isBeginning,
    isEnd,
    scrollSliderLeft,
    scrollSliderRight,
    attachScrollListeners,
    detachScrollListeners,
  } = useScrollableCarousel();

  useEffect(() => {
    attachScrollListeners();
    return () => detachScrollListeners();
  }, [attachScrollListeners, detachScrollListeners]);

  const { subjectProperty, comparableProperties } = useMemo(() => {
    if (!properties) return { subjectProperty: null, comparableProperties: [] };
    if (properties.length === 0)
      return { subjectProperty: null, comparableProperties: [] };
    const comparableProperties = properties.slice(1);
    const subjectProperty = properties[0];
    return { subjectProperty, comparableProperties };
  }, [properties]);

  const subjectPropertyContent = subjectProperty ? (
    <ComparisonPropertyItem
      propertyData={subjectProperty}
      gallery={galleries[subjectProperty.id]}
      isSubjectProperty
      preferences={preferences}
    />
  ) : undefined;

  return (
    <Box
      id={id}
      py={3}
      mt={2}
      marginX="auto"
      textAlign="center"
      maxWidth={"lg"}
    >
      <Typography variant="h5">Comparable Properties</Typography>

      <SideBySideComparisonList>
        <SideBySideComparisonHeader>
          <NavButtonsContainer>
            <NavButton disabled={isBeginning} onClick={scrollSliderLeft}>
              <ChevronLeftIcon />
            </NavButton>
            <NavButton disabled={isEnd} onClick={scrollSliderRight}>
              <ChevronRightIcon />
            </NavButton>
          </NavButtonsContainer>
          <Typography variant="overline">
            {comparableProperties.length}{" "}
            {comparableProperties.length === 1 ? "property" : "properties"}
          </Typography>
        </SideBySideComparisonHeader>

        <SideBySideComparisonCarousel
          containerRef={containerRef}
          subjectPropertyContent={subjectPropertyContent}
        >
          {comparableProperties.map((property) => (
            <SlideItem key={property.id}>
              <ComparisonPropertyItem
                propertyData={property}
                gallery={galleries[property.id]}
                isSubjectProperty={false}
                preferences={preferences}
              />
            </SlideItem>
          ))}
        </SideBySideComparisonCarousel>
      </SideBySideComparisonList>
    </Box>
  );
};
